import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Loader2, Clapperboard, Sparkles, AlertCircle } from 'lucide-react';

const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5'];
const newRange = () => ({ key: Math.random().toString(36).slice(2), start: 0, end: 10, name: '' });

/**
 * Manual + AI-assisted LF/SF generation from the APPROVED Master. Every
 * range gets sliced server-side out of the frozen approved snapshot
 * (never the live draft, never a raw source timestamp) — this panel
 * only collects the ranges/options and shows the real result.
 */
export function EditorClipGenerationPanel({
  disabled, approvedDuration, onGenerate, generating,
  acceptedHighlightSuggestions, onGenerateFromSuggestions,
  markers,
}) {
  const [outputType, setOutputType] = useState('sf');
  const [ranges, setRanges] = useState([newRange()]);
  const [aspectRatio, setAspectRatio] = useState('inherit');
  const [inheritCaptions, setInheritCaptions] = useState(true);
  const [inheritBranding, setInheritBranding] = useState(true);
  const [namePrefix, setNamePrefix] = useState('');
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  if (disabled) {
    return (
      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex gap-2" data-testid="clip-generation-locked">
        <AlertCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200">Approve the Master first — clip generation always cuts from the exact approved version, never a draft.</p>
      </div>
    );
  }

  const updateRange = (key, partial) => setRanges((rs) => rs.map((r) => (r.key === key ? { ...r, ...partial } : r)));
  const addRange = () => setRanges((rs) => [...rs, { ...newRange(), start: rs[rs.length - 1]?.end || 0, end: (rs[rs.length - 1]?.end || 0) + 10 }]);
  const removeRange = (key) => setRanges((rs) => rs.filter((r) => r.key !== key));

  // Markers are user-provided CLIP GUIDANCE, not automatic boundaries —
  // this seeds the same editable `ranges` list below (start = each
  // marker, end = the next marker or the approved duration) so every
  // value can still be freely adjusted or removed before generating.
  const seedFromMarkers = () => {
    const sorted = [...markers].sort((a, b) => a.elapsed_seconds - b.elapsed_seconds);
    setRanges(sorted.map((m, i) => ({
      key: Math.random().toString(36).slice(2),
      start: m.elapsed_seconds,
      end: Math.min(approvedDuration || Infinity, sorted[i + 1]?.elapsed_seconds ?? (m.elapsed_seconds + 10)),
      name: m.label,
    })));
  };

  const submit = () => {
    onGenerate({
      output_type: outputType,
      ranges: ranges.map((r) => ({ start_seconds: r.start, end_seconds: r.end, name: r.name || undefined })),
      target_aspect_ratio: aspectRatio === 'inherit' ? null : aspectRatio,
      inherit_captions: inheritCaptions,
      inherit_branding: inheritBranding,
      name_prefix: namePrefix || undefined,
    });
  };

  const toggleSuggestion = (id) => setSelectedSuggestions((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submitFromSuggestions = () => {
    if (selectedSuggestions.length === 0) return;
    onGenerateFromSuggestions({
      suggestion_ids: selectedSuggestions,
      output_type: outputType,
      target_aspect_ratio: aspectRatio === 'inherit' ? null : aspectRatio,
      inherit_captions: inheritCaptions,
      inherit_branding: inheritBranding,
    });
    setSelectedSuggestions([]);
  };

  return (
    <div className="space-y-3" data-testid="clip-generation-panel">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-[#8A93A6] mb-1 block">Output type</label>
          <Select value={outputType} onValueChange={setOutputType}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="clip-gen-output-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lf">Long Form</SelectItem>
              <SelectItem value="sf">Short Form</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-[#8A93A6] mb-1 block">Aspect ratio</label>
          <Select value={aspectRatio} onValueChange={setAspectRatio}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="clip-gen-aspect-ratio"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="inherit">Inherit Master</SelectItem>
              {ASPECT_RATIOS.map((ar) => <SelectItem key={ar} value={ar}>{ar}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Input
        value={namePrefix} onChange={(e) => setNamePrefix(e.target.value)} placeholder="Clip name prefix (optional)"
        className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="clip-gen-name-prefix"
      />

      <div className="flex items-center gap-4 text-xs text-[#8A93A6]">
        <label className="flex items-center gap-1.5 cursor-pointer"><Checkbox checked={inheritCaptions} onCheckedChange={setInheritCaptions} data-testid="clip-gen-inherit-captions" />Inherit captions</label>
        <label className="flex items-center gap-1.5 cursor-pointer"><Checkbox checked={inheritBranding} onCheckedChange={setInheritBranding} data-testid="clip-gen-inherit-branding" />Inherit branding</label>
      </div>

      <div className="space-y-1.5" data-testid="clip-gen-ranges">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Ranges on the approved Master (0–{(approvedDuration || 0).toFixed(1)}s)</p>
          {markers?.length > 0 && (
            <button onClick={seedFromMarkers} className="text-[10px] text-[#3A7BFF] hover:text-white" data-testid="clip-gen-seed-from-markers">
              Use {markers.length} Marker{markers.length > 1 ? 's' : ''}
            </button>
          )}
        </div>
        {ranges.map((r) => (
          <div key={r.key} className="flex items-center gap-1.5" data-testid={`clip-gen-range-${r.key}`}>
            <Input type="number" step="0.1" value={r.start} onChange={(e) => updateRange(r.key, { start: Number(e.target.value) })} className="h-7 w-20 bg-[#1C1F26] border-white/10 text-white text-xs" />
            <span className="text-[#555E6E] text-xs">–</span>
            <Input type="number" step="0.1" value={r.end} onChange={(e) => updateRange(r.key, { end: Number(e.target.value) })} className="h-7 w-20 bg-[#1C1F26] border-white/10 text-white text-xs" />
            <Input value={r.name} onChange={(e) => updateRange(r.key, { name: e.target.value })} placeholder="Name (optional)" className="h-7 flex-1 bg-[#1C1F26] border-white/10 text-white text-xs" />
            <button onClick={() => removeRange(r.key)} disabled={ranges.length === 1} className="text-red-400 hover:text-red-300 disabled:opacity-20" data-testid={`clip-gen-remove-range-${r.key}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="w-full border-white/10 h-7 text-xs" onClick={addRange} data-testid="clip-gen-add-range">
          <Plus className="h-3.5 w-3.5 mr-1.5" />Add another range (batch)
        </Button>
      </div>

      <Button onClick={submit} disabled={generating} className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" data-testid="clip-gen-submit">
        {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Clapperboard className="h-4 w-4 mr-2" />}
        Generate {ranges.length > 1 ? `${ranges.length} Clips` : 'Clip'}
      </Button>

      <div className="pt-2 border-t border-white/10 space-y-1.5" data-testid="clip-gen-ai-section">
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">From accepted AI Best Moments</p>
        {(!acceptedHighlightSuggestions || acceptedHighlightSuggestions.length === 0) && (
          <p className="text-[11px] text-[#555E6E]" data-testid="clip-gen-no-ai-suggestions">No accepted highlights yet — run Best Moments in the AI tab once an analysis provider is connected.</p>
        )}
        {acceptedHighlightSuggestions?.map((s) => (
          <label key={s.id} className="flex items-center gap-2 p-1.5 rounded bg-white/5 text-xs cursor-pointer" data-testid={`clip-gen-ai-suggestion-${s.id}`}>
            <Checkbox checked={selectedSuggestions.includes(s.id)} onCheckedChange={() => toggleSuggestion(s.id)} />
            <span className="text-white truncate flex-1">{s.label || 'Highlight'}</span>
            {s.start_seconds != null && <span className="text-[#8A93A6]">{s.start_seconds.toFixed(1)}s–{(s.end_seconds ?? s.start_seconds).toFixed(1)}s</span>}
          </label>
        ))}
        {acceptedHighlightSuggestions?.length > 0 && (
          <Button
            size="sm" variant="outline" className="w-full border-white/10 text-xs h-7" disabled={generating || selectedSuggestions.length === 0}
            onClick={submitFromSuggestions} data-testid="clip-gen-from-ai-submit"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate from {selectedSuggestions.length || ''} Selected
          </Button>
        )}
      </div>
    </div>
  );
}
